/**
 * MusicSystem.js — Procedural music engine for CannTycoon
 * All audio synthesized via Web Audio API, no files needed.
 *
 * Tracks:
 *   'gameplay' — 100 BPM trap/lo-fi groove, 2-bar loop
 *   'menu'     — 88 BPM chill ambient, 2-bar loop
 *   'achieve'  — one-shot ascending arp + chord sting
 */

// ── Note frequencies (Hz) ────────────────────────────────────────────────────
const N = {
  A1: 55.00, E2: 82.41, G2: 98.00,
  A2: 110.00, C3: 130.81, D3: 146.83, E3: 164.81, G3: 196.00,
  A3: 220.00, C4: 261.63, D4: 293.66, E4: 329.63, G4: 392.00,
  A4: 440.00, C5: 523.25, E5: 659.25,
};

// ── Track definitions ─────────────────────────────────────────────────────────
// 32 steps = 2 bars of 16th notes. 0/null = rest.

const GAMEPLAY = {
  bpm: 100,
  // Trap kick: 1, ghost on &2, 3, ghost on &4+
  kick:  [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,1,0,0,  1,0,0,0, 0,0,1,0, 1,0,1,0, 0,0,0,0],
  // Snare: 2, 4 with ghost on last 16th of bar 2
  snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0,  0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,1],
  // Closed hi-hat: 8th notes with some 16th fills
  hihat: [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,1,  1,0,1,0, 1,0,1,1, 1,0,1,0, 1,0,1,0],
  // Open hi-hat: on the & of 2 and & of 4
  ohhat: [0,0,0,0, 0,0,0,1, 0,0,0,0, 0,0,0,0,  0,0,0,0, 0,0,0,1, 0,0,0,0, 0,0,0,0],
  // Bass: A minor pentatonic, 808-style
  bass: [
    N.A2,0,0,0,     0,0,N.A2,0,   N.A2,0,0,N.C3,  0,0,0,0,
    N.A2,0,0,0,     N.G3,0,0,0,   N.E3,0,0,0,      N.A2,N.D3,0,0,
  ],
  // Melody: sparse motif, A minor pent
  melody: [
    0,0,0,0,        N.E4,0,0,0,   N.D4,0,0,0,      0,0,0,0,
    0,0,N.A3,0,     0,0,0,0,      N.C4,0,N.D4,0,   N.E4,0,0,0,
  ],
};

const MENU = {
  bpm: 88,
  // Simpler kick — just 1 and 3
  kick:  [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0,  1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,1,0],
  // Snare: 2, 4
  snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0,  0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
  // Sparse hi-hat
  hihat: [1,0,0,1, 0,0,1,0, 0,1,0,0, 1,0,0,0,  1,0,0,1, 0,0,1,0, 0,1,0,0, 1,0,0,0],
  ohhat: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,1,0,  0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0],
  bass: [
    N.A2,0,0,0,  0,0,0,0,  N.A2,0,0,0,  N.C3,0,0,0,
    N.D3,0,0,0,  0,0,0,0,  N.E3,0,0,0,  N.A2,0,0,0,
  ],
  melody: [
    N.A3,0,0,0,  0,0,0,0,  N.C4,0,0,0,  N.D4,0,0,0,
    N.E4,0,0,0,  N.D4,0,0,0,  N.C4,0,0,0,  N.A3,0,0,0,
  ],
};

// ── Engine ───────────────────────────────────────────────────────────────────

class MusicSystem {
  constructor() {
    this._ctx        = null;
    this._master     = null;
    this._noise      = null;   // shared white noise buffer
    this._playing    = false;
    this._trackName  = null;
    this._trackDef   = null;
    this._step       = 0;
    this._nextTime   = 0;
    this._timerId    = null;
    this._volume     = 0.35;
    this._muted      = false;
  }

  // ── Setup ──────────────────────────────────────────────────────────────────

  _init() {
    if (this._ctx) return;
    this._ctx    = new (window.AudioContext || window.webkitAudioContext)();
    this._master = this._ctx.createGain();
    this._master.gain.value = this._muted ? 0 : this._volume;
    this._master.connect(this._ctx.destination);
    this._noise  = this._buildNoise();
  }

  _buildNoise() {
    const sr  = this._ctx.sampleRate;
    const buf = this._ctx.createBuffer(1, sr * 2, sr);
    const d   = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }

  _stepDur(bpm) { return 60 / bpm / 4; } // 16th-note duration in seconds

  // ── Instruments ────────────────────────────────────────────────────────────

  _kick(t) {
    const { ctx: c, master: m } = { ctx: this._ctx, master: this._master };
    const osc  = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain); gain.connect(m);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(42, t + 0.35);
    gain.gain.setValueAtTime(0.95, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.42);
    osc.start(t); osc.stop(t + 0.42);
  }

  _snare(t) {
    const c = this._ctx, m = this._master;

    // Noise body
    const ns  = c.createBufferSource();
    ns.buffer = this._noise;
    const bf  = c.createBiquadFilter();
    bf.type   = 'bandpass'; bf.frequency.value = 1800; bf.Q.value = 0.9;
    const ng  = c.createGain();
    ns.connect(bf); bf.connect(ng); ng.connect(m);
    ng.gain.setValueAtTime(0.55, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    ns.start(t); ns.stop(t + 0.18);

    // Tone click
    const osc = c.createOscillator();
    const og  = c.createGain();
    osc.connect(og); og.connect(m);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.exponentialRampToValueAtTime(90, t + 0.08);
    og.gain.setValueAtTime(0.38, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    osc.start(t); osc.stop(t + 0.08);
  }

  _hihat(t, open = false) {
    const c = this._ctx, m = this._master;
    const ns  = c.createBufferSource();
    ns.buffer = this._noise;
    const hp  = c.createBiquadFilter();
    hp.type   = 'highpass'; hp.frequency.value = 7500;
    const gain = c.createGain();
    ns.connect(hp); hp.connect(gain); gain.connect(m);
    const dec = open ? 0.24 : 0.045;
    const vol = open ? 0.22 : 0.14;
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dec);
    ns.start(t); ns.stop(t + dec);
  }

  _bass(t, freq, stepDur) {
    const c = this._ctx, m = this._master;
    const osc = c.createOscillator();
    const lp  = c.createBiquadFilter();
    const gain = c.createGain();
    osc.connect(lp); lp.connect(gain); gain.connect(m);
    osc.type = 'sine';
    osc.frequency.value = freq;
    lp.type = 'lowpass'; lp.frequency.value = 380;
    const dur = stepDur * 2.2;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.7, t + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.start(t); osc.stop(t + dur);
  }

  _synth(t, freq, stepDur, vol = 0.16) {
    const c = this._ctx, m = this._master;
    const osc  = c.createOscillator();
    const lp   = c.createBiquadFilter();
    const gain = c.createGain();
    osc.connect(lp); lp.connect(gain); gain.connect(m);
    osc.type = 'triangle';
    osc.frequency.value = freq;
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(2200, t);
    lp.frequency.exponentialRampToValueAtTime(700, t + stepDur * 2.5);
    const dur = stepDur * 3.5;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + 0.018);
    gain.gain.setValueAtTime(vol, t + stepDur * 1.5);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.start(t); osc.stop(t + dur + 0.05);
  }

  // ── Scheduler ─────────────────────────────────────────────────────────────

  _schedule() {
    if (!this._playing) return;
    const def      = this._trackDef;
    const stepDur  = this._stepDur(def.bpm);
    const total    = def.kick.length;
    const lookahead = 0.12; // seconds ahead to schedule

    while (this._nextTime < this._ctx.currentTime + lookahead) {
      const t = this._nextTime;
      const s = this._step;

      if (def.kick[s])              this._kick(t);
      if (def.snare[s])             this._snare(t);
      if (def.hihat[s])             this._hihat(t, false);
      if (def.ohhat && def.ohhat[s]) this._hihat(t, true);
      if (def.bass[s])              this._bass(t, def.bass[s], stepDur);
      if (def.melody && def.melody[s]) this._synth(t, def.melody[s], stepDur);

      this._step      = (s + 1) % total;
      this._nextTime += stepDur;
    }

    this._timerId = setTimeout(() => this._schedule(), 22);
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  play(trackName) {
    this._init();
    if (this._ctx.state === 'suspended') this._ctx.resume();
    if (this._playing && this._trackName === trackName) return;
    this.stop();

    this._trackName = trackName;
    this._trackDef  = trackName === 'menu' ? MENU : GAMEPLAY;
    this._step      = 0;
    this._nextTime  = this._ctx.currentTime + 0.05;
    this._playing   = true;
    this._schedule();
  }

  stop() {
    clearTimeout(this._timerId);
    this._timerId  = null;
    this._playing  = false;
    this._trackName = null;
  }

  /** One-shot achievement sting: ascending arp then resolution chord */
  achieve() {
    this._init();
    if (this._ctx.state === 'suspended') this._ctx.resume();
    const c = this._ctx;
    const m = this._master;
    const t0 = c.currentTime + 0.04;

    // Rising arpeggio
    [N.A3, N.C4, N.E4, N.A4].forEach((freq, i) => {
      const osc  = c.createOscillator();
      const lp   = c.createBiquadFilter();
      const gain = c.createGain();
      osc.connect(lp); lp.connect(gain); gain.connect(m);
      osc.type = 'triangle';
      osc.frequency.value = freq;
      lp.type = 'bandpass'; lp.frequency.value = freq * 1.8; lp.Q.value = 1.2;
      const nt = t0 + i * 0.11;
      gain.gain.setValueAtTime(0, nt);
      gain.gain.linearRampToValueAtTime(0.28, nt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, nt + 0.45);
      osc.start(nt); osc.stop(nt + 0.45);
    });

    // Resolution chord (A minor: A C E)
    [N.A3, N.C4, N.E4, N.A4].forEach(freq => {
      const osc  = c.createOscillator();
      const lp   = c.createBiquadFilter();
      const gain = c.createGain();
      osc.connect(lp); lp.connect(gain); gain.connect(m);
      osc.type = 'sine';
      osc.frequency.value = freq;
      lp.type = 'lowpass'; lp.frequency.value = 1800;
      const ct = t0 + 0.46;
      gain.gain.setValueAtTime(0, ct);
      gain.gain.linearRampToValueAtTime(0.18, ct + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, ct + 1.4);
      osc.start(ct); osc.stop(ct + 1.4);
    });
  }

  setVolume(v) {
    this._volume = v;
    if (this._master && !this._muted) this._master.gain.value = v;
  }

  setMuted(muted) {
    this._muted = muted;
    if (this._master) this._master.gain.value = muted ? 0 : this._volume;
  }

  get muted()     { return this._muted; }
  get isPlaying() { return this._playing; }
  get track()     { return this._trackName; }
}

export const music = new MusicSystem();
